import React, { useMemo } from 'react';

import { FontAwesomeIcon } from '@appcharge/shared-ui';

import InputSelectDropdown, {
    InputSelectDropdownOption
} from 'components/InputSelectDropdown/InputSelectDropdown';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';

import { PROJECT_TYPE_FILTERS, SORT_OPTIONS, ViewMode } from '../constants';
import { SortOption } from '../types';

interface ProjectsFiltersProps {
    searchTerm: string;
    onSearchChange: (value: string) => void;
    selectedFilter: string;
    onFilterChange: (value: string) => void;
    sortBy: SortOption | '';
    onSortChange: (value: SortOption) => void;
    viewMode: ViewMode;
    onViewModeChange: (mode: ViewMode) => void;
}

export const ProjectsFilters: React.FC<ProjectsFiltersProps> = ({
    searchTerm,
    onSearchChange,
    selectedFilter,
    onFilterChange,
    sortBy,
    onSortChange,
    viewMode,
    onViewModeChange
}) => {
    const filterOptions: InputSelectDropdownOption[] = useMemo(() =>
        PROJECT_TYPE_FILTERS.map(filter => ({
            value: filter.value,
            label: filter.label
        })), []);

    const filterIcon = useMemo(() => <FontAwesomeIcon icon="fa-solid fa-bars-filter" className="text-[#404040]" />, []);

    const sortOptions: InputSelectDropdownOption[] = useMemo(() =>
        SORT_OPTIONS.map(option => ({
            value: option.value,
            label: option.label,
            disabled: option.disabled
        })), []);

    const selectedFilterOption = useMemo(() =>
        filterOptions.find(option => option.value === selectedFilter),
        [filterOptions, selectedFilter]);

    const selectedSortOption = useMemo(() =>
        sortBy ? {
            ...sortOptions.find(option => option.value === sortBy)!,
            rightIcon: filterIcon
        } : undefined,
        [sortOptions, sortBy, filterIcon]);

    return (
        <div className="flex items-center gap-4">
            {/* Search Input */}
            <div className="relative">
                <FontAwesomeIcon
                    icon="fa-regular fa-search"
                    className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4 z-10"
                />
                <Input
                    type="text"
                    placeholder="Search..."
                    value={searchTerm}
                    onChange={(e) => onSearchChange(e.target.value)}
                    className="pl-10 w-[190px] min-[1100px]:w-[248px] h-10"
                />
            </div>

            {/* Filters and View Toggle */}
            <div className="flex items-center gap-3">
                <div className="w-[160px]">
                    <InputSelectDropdown
                        placeholder="All projects"
                        options={filterOptions}
                        defaultSelected={selectedFilterOption}
                        onOptionChange={onFilterChange}
                    />
                </div>
                <div className="w-[140px]">
                    <InputSelectDropdown
                        placeholder="Sort by"
                        options={sortOptions}
                        defaultSelected={selectedSortOption}
                        onOptionChange={(value) => onSortChange(value as SortOption)}
                    />
                </div>

                <div className="flex bg-gray-100 rounded-lg p-1 h-9">
                    <Button
                        variant="ghost"
                        size="sm"
                        className={`w-7 h-7 p-0 rounded-md ${viewMode === ViewMode.GRID
                            ? "bg-white shadow-sm"
                            : "bg-transparent hover:bg-gray-200"
                            }`}
                        onClick={() => onViewModeChange(ViewMode.GRID)}
                    >
                        <FontAwesomeIcon
                            icon="fa-regular fa-grid-2"
                            className={`w-[12px] h-[12px] ${viewMode === ViewMode.GRID ? "text-base-foreground" : "text-base-mutedForeground"}`}
                        />
                    </Button>
                    <Button
                        variant="ghost"
                        size="sm"
                        className={`w-7 h-7 p-0 rounded-md ${viewMode === ViewMode.LIST
                            ? "bg-white shadow-sm"
                            : "bg-transparent hover:bg-gray-200"
                            }`}
                        onClick={() => onViewModeChange(ViewMode.LIST)}
                    >
                        <FontAwesomeIcon
                            icon="fa-solid fa-list-ul"
                            className={`w-[12px] h-[12px] ${viewMode === ViewMode.LIST ? "text-base-foreground" : "text-base-mutedForeground"}`}
                        />
                    </Button>
                </div>
            </div>
        </div>
    );
};
